import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const OfficeSection = () => {
  const [activeTab, setActiveTab] = useState('overview');

  const officeImages = [
  {
    id: 1,
    src: "https://images.unsplash.com/photo-1637621982007-44aaf9f9b0c6",
    alt: "Modern open office space with glass walls, contemporary furniture and natural lighting",
    title: "Main Office Space"
  },
  {
    id: 2,
    src: "https://images.unsplash.com/photo-1673519451881-6d801cad9040",
    alt: "Professional conference room with large table, modern chairs and city view windows",
    title: "Conference Room"
  },
  {
    id: 3,
    src: "https://images.unsplash.com/photo-1728581665814-684c66cb036d",
    alt: "Casual meeting area with comfortable seating and collaborative workspace design",
    title: "Collaboration Space"
  },
  {
    id: 4,
    src: "https://images.unsplash.com/photo-1650130071434-b6b98b4ff4d8",
    alt: "Hamburg Speicherstadt historic brick buildings with modern glass architecture",
    title: "Speicherstadt Location"
  }];


  const officeFeatures = [
  {
    icon: "MapPin",
    title: "Prime Location",
    description: "Located in Hamburg's historic Speicherstadt, the world's largest warehouse district and UNESCO World Heritage site."
  },
  {
    icon: "Users",
    title: "Collaborative Environment",
    description: "Open-plan design fostering innovation and teamwork, with dedicated spaces for focused work and creative sessions."
  },
  {
    icon: "Wifi",
    title: "Advanced Technology",
    description: "State-of-the-art IT infrastructure, high-speed connectivity, and modern presentation facilities for seamless operations."
  },
  {
    icon: "Coffee",
    title: "Wellness Focus",
    description: "Ergonomic workstations, natural lighting, recreational areas, and premium coffee to support team well-being."
  },
  {
    icon: "Car",
    title: "Accessibility",
    description: "Excellent public transport connections, parking facilities, and proximity to Hamburg's business district."
  },
  {
    icon: "Shield",
    title: "Security & Privacy",
    description: "Advanced security systems and private meeting rooms ensuring confidential discussions and data protection."
  }];


  const tabs = [
  { id: 'overview', label: 'Overview', icon: 'Building2' },
  { id: 'gallery', label: 'Gallery', icon: 'Camera' },
  { id: 'location', label: 'Location', icon: 'MapPin' }];


  return (
    <section className="py-20 bg-background">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center max-w-4xl mx-auto mb-16">
          <div className="flex items-center justify-center space-x-3 mb-6">
            <div className="w-10 h-10 rounded-brand bg-gradient-to-br from-primary to-accent flex items-center justify-center">
              <Icon name="Building2" size={20} className="text-white" />
            </div>
            <span className="text-primary font-semibold text-lg">Hamburg Office</span>
          </div>
          
          <h2 className="text-4xl lg:text-5xl font-bold text-foreground mb-6">
            Where Innovation
            <span className="text-gradient-brand block">Meets Tradition</span>
          </h2>
          
          <p className="text-xl text-muted-foreground leading-relaxed">
            Our Hamburg headquarters in the historic Speicherstadt combines the rich heritage of 
            European commerce with cutting-edge technology and modern workspace design.
          </p>
        </div>

        {/* Tabs */}
        <div className="flex justify-center mb-12">
          <div className="bg-muted rounded-brand p-1 inline-flex">
            {tabs?.map((tab) =>
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center space-x-2 px-6 py-3 rounded-brand-sm font-medium transition-brand ${
              activeTab === tab?.id ?
              'bg-primary text-primary-foreground shadow-brand-sm' :
              'text-muted-foreground hover:text-foreground'}`
              }>

                <Icon name={tab?.icon} size={16} />
                <span>{tab?.label}</span>
              </button>
            )}
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' &&
        <div className="space-y-16">
            {/* Features Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {officeFeatures?.map((feature, index) =>
            <div
              key={index}
              className={`bg-card rounded-brand-lg p-6 shadow-brand hover-lift transition-brand border border-border scroll-reveal stagger-${index % 3 + 1}`}>

                  <div className="w-12 h-12 rounded-brand bg-gradient-to-br from-primary to-accent flex items-center justify-center mb-4">
                    <Icon name={feature?.icon} size={20} className="text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-3">{feature?.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{feature?.description}</p>
                </div>
            )}
            </div>

            {/* Office Stats */}
            <div className="bg-gradient-to-br from-primary/5 to-accent/5 rounded-brand-lg p-8 lg:p-12 border border-primary/10">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
                <div>
                  <div className="text-3xl font-bold text-primary counter-animate">2,500</div>
                  <div className="text-sm text-muted-foreground font-medium">Square Meters</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-primary counter-animate">50+</div>
                  <div className="text-sm text-muted-foreground font-medium">Team Members</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-primary counter-animate">8</div>
                  <div className="text-sm text-muted-foreground font-medium">Meeting Rooms</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-primary counter-animate">24/7</div>
                  <div className="text-sm text-muted-foreground font-medium">Access Available</div>
                </div>
              </div>
            </div>
          </div>
        }

        {activeTab === 'gallery' &&
        <div className="grid md:grid-cols-2 gap-8">
            {officeImages?.map((image, index) =>
          <div
            key={image?.id}
            className={`group relative rounded-brand-lg overflow-hidden shadow-brand hover-lift transition-brand scroll-reveal stagger-${index % 2 + 1}`}>

                <Image
              src={image?.src}
              alt={image?.alt}
              className="w-full h-64 lg:h-80 object-cover group-hover:scale-105 transition-brand" />

                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-brand"></div>
                <div className="absolute bottom-6 left-6 right-6 opacity-0 group-hover:opacity-100 transition-brand">
                  <h3 className="text-white font-semibold text-lg">{image?.title}</h3>
                </div>
              </div>
          )}
          </div>
        }

        {activeTab === 'location' &&
        <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Map */}
            <div className="rounded-brand-lg overflow-hidden shadow-brand border border-border">
              <iframe
              width="100%"
              height="400"
              loading="lazy"
              title="REXFEL Hamburg Office Location"
              referrerPolicy="no-referrer-when-downgrade"
              src="https://www.google.com/maps?q=53.5438,9.9998&z=14&output=embed"
              className="w-full h-96 lg:h-[400px]">
            </iframe>
            </div>

            {/* Location Details */}
            <div className="space-y-8">
              <div>
                <h3 className="text-2xl font-bold text-foreground mb-4">Visit Our Office</h3>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  Located in the heart of Hamburg's Speicherstadt, our office combines historic charm with modern functionality. We're easily accessible by public transport 
                  and offer a welcoming environment for meetings and collaborations.
                </p>
              </div>

              <div className="space-y-4">
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 rounded-brand bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Icon name="MapPin" size={18} className="text-primary" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground mb-1">Address</h4>
                    <p className="text-muted-foreground">
                      Speicherstadt 1<br />
                      20457 Hamburg, Germany
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 rounded-brand bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Icon name="Clock" size={18} className="text-primary" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground mb-1">Office Hours</h4>
                    <p className="text-muted-foreground">
                      Monday - Friday: 9:00 AM - 6:00 PM<br />
                      Weekend: By appointment
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 rounded-brand bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Icon name="Train" size={18} className="text-primary" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground mb-1">Public Transport</h4>
                    <p className="text-muted-foreground">
                      U-Bahn: Baumwall (U3)<br />
                      S-Bahn: Stadthausbrücke (S1, S3)
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 rounded-brand bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Icon name="Phone" size={18} className="text-primary" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground mb-1">Contact</h4>
                    <p className="text-muted-foreground">
                      +49 40 123 456 789<br />
                      office@rexfel.com
                    </p>
                  </div>
                </div>
              </div>

              <div className="pt-6 border-t border-border">
                <button className="w-full bg-gradient-to-r from-primary to-accent text-white py-3 px-6 rounded-brand font-medium hover:from-primary/90 hover:to-accent/90 transition-brand flex items-center justify-center space-x-2">
                  <Icon name="Calendar" size={18} />
                  <span>Schedule a Visit</span>
                </button>
              </div>
            </div>
          </div>
        }
      </div>
    </section>);

};

export default OfficeSection;